<template>
  <div class="flex flex-col w-full">
    <div v-show="isScanning" class="flex flex-col items-center mt-4">
      <div class="reader-container">
        <video ref="videoRef" class="reader-video" autoplay muted playsinline></video>
        <div class="scan-guide" aria-hidden="true">
          <div class="scan-window">
            <span class="corner corner-tl"></span>
            <span class="corner corner-tr"></span>
            <span class="corner corner-bl"></span>
            <span class="corner corner-br"></span>
          </div>
        </div>
        <div v-if="isWasmLoading" class="scanner-overlay">
          <div class="spinner"></div>
          <div class="overlay-text">バーコードエンジンを初期化中...</div>
        </div>
      </div>
      <button @click="stopScanning" class="stop-button mt-2">スキャンを停止</button>
    </div>

    <div v-if="errorMessage" class="error-message mt-4">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onBeforeUnmount, nextTick } from 'vue';
import { prepareZXingModule, readBarcodes, type ReaderOptions } from 'zxing-wasm/reader';

type ScanMode = 'auto' | 'jancode' | 'code128';
type CodeScannedMeta = {
  rawText: string;
  format: string;
  expirationDate: string | null;
};

/** JAN（EAN/UPC 系）与 Code128 同时启用 */
const JAN_AND_CODE128_FORMATS: ReaderOptions['formats'] = [
  'EAN13',
  'EAN8',
  'UPCA',
  'UPCE',
  'Code128',
];

const MODE_FORMATS: Record<ScanMode, ReaderOptions['formats']> = {
  auto: JAN_AND_CODE128_FORMATS,
  jancode: JAN_AND_CODE128_FORMATS,
  code128: ['Code128'],
};

const isCode128BarcodeFormat = (format: string) =>
  typeof format === 'string' &&
  (format === 'Code128' || format.replace(/\s+/g, '').toUpperCase().includes('CODE128'));

const JAN13_BODY_WEIGHTS = [1, 3, 1, 3, 1, 3, 1, 3, 1, 3, 1, 3] as const;

/**
 * 12 桁本体から EAN/JAN13 のチェックデジットを算出する。
 */
function calcJan13CheckDigit(jan12: string): string {
  const sum = jan12
    .split('')
    .reduce((acc, ch, idx) => acc + Number(ch) * JAN13_BODY_WEIGHTS[idx], 0);
  return String((10 - (sum % 10)) % 10);
}

/**
 * GTIN14 を JAN13 へ正規化する（先頭1桁を除外し、チェックデジットを再計算）。
 * Excel ロジック:
 *   MID(TEXT(x,"00000000000000"),2,12) & MOD(10-MOD(weightedSum,10),10)
 */
function normalizeGtin14ToJan13(gtin14: string): string | null {
  if (!/^\d{14}$/.test(gtin14)) {
    return null;
  }
  const jan12 = gtin14.substring(1, 13);
  return `${jan12}${calcJan13CheckDigit(jan12)}`;
}

/**
 * Code128 / GS1-128 から JAN13 検索コードを抽出する。
 * 優先順:
 * 1) (01) + 14桁
 * 2) 先頭 AI "01" + 14桁（括弧なし）
 * 3) 生の 14桁のみ
 */
function extractJanFromCode128(raw: string): string | null {
  const trimmed = (raw || '').trim();

  const aiWithBrackets = trimmed.match(/\(01\)(\d{14})/);
  if (aiWithBrackets?.[1]) {
    return normalizeGtin14ToJan13(aiWithBrackets[1]);
  }

  const digitsOnly = trimmed.replace(/[^\d]/g, '');
  const aiWithoutBrackets = digitsOnly.match(/01(\d{14})/);
  if (aiWithoutBrackets?.[1]) {
    return normalizeGtin14ToJan13(aiWithoutBrackets[1]);
  }

  if (/^\d{14}$/.test(digitsOnly)) {
    return normalizeGtin14ToJan13(digitsOnly);
  }

  return null;
}

function parseGs1YymmddToIso(yymmdd: string): string | null {
  if (!/^\d{6}$/.test(yymmdd)) {
    return null;
  }

  const yy = Number(yymmdd.substring(0, 2));
  const mmRaw = Number(yymmdd.substring(2, 4));
  const ddRaw = Number(yymmdd.substring(4, 6));
  if (Number.isNaN(yy) || Number.isNaN(mmRaw) || Number.isNaN(ddRaw)) {
    return null;
  }

  // Business rule: MM=00 -> 12, DD=00 -> last day of month.
  const mm = mmRaw === 0 ? 12 : mmRaw;
  const lastDayOfMonth = new Date(Date.UTC(2000 + yy, mm, 0)).getUTCDate();
  const dd = ddRaw === 0 ? lastDayOfMonth : ddRaw;
  if (mm < 1 || mm > 12) {
    return null;
  }

  const fullYear = 2000 + yy;
  if (dd < 1 || dd > lastDayOfMonth) {
    return null;
  }

  return new Date(Date.UTC(fullYear, mm - 1, dd)).toISOString();
}

/**
 * GS1 AI (17)/(15) または 17YYMMDD/15YYMMDD から日付（ISO）を抽出する。
 * - 優先: (17) > (15) > 17 > 15
 * - YY は 2000-2099
 * - MM=00 は 12、DD=00 は当月末日として扱う
 */
function extractExpirationDateFromCode128(raw: string): string | null {
  const trimmed = (raw || '').trim();
  const withBrackets = trimmed.match(/\((17|15)\)(\d{6})/);
  if (withBrackets?.[2]) {
    return parseGs1YymmddToIso(withBrackets[2]);
  }

  const digitsOnly = trimmed.replace(/[^\d]/g, '');
  const withoutBrackets = digitsOnly.match(/(17|15)(\d{6})/);
  if (withoutBrackets?.[2]) {
    return parseGs1YymmddToIso(withoutBrackets[2]);
  }

  return null;
}

export default defineComponent({
  name: 'BarcodeScanner',
  props: {
    modelValue: {
      type: String,
      default: '',
    },
    /** auto / jancode: EAN・UPC 系 + Code128。code128: Code128 のみ */
    scanMode: {
      type: String as () => ScanMode,
      default: 'auto',
    },
  },
  emits: ['update:modelValue', 'code-scanned', 'scan-stopped'],
  setup(props, { emit }) {
    const isScanning = ref(false);
    const scannedResult = ref('');
    const detectedFormat = ref('');
    const errorMessage = ref('');
    const videoRef = ref<HTMLVideoElement | null>(null);
    const streamRef = ref<MediaStream | null>(null);
    const rafRef = ref<number | null>(null);
    const decodingRef = ref(false);
    const lastDecodeAt = ref(0);
    const wasmReady = ref(false);
    const isWasmLoading = ref(false);
    const wasmInitPromiseRef = ref<Promise<void> | null>(null);

    const stableCandidate = ref('');
    const stableCount = ref(0);
    const REQUIRED_STABLE_COUNT = 2;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d', { willReadFrequently: true });

    const ensureWasmReady = async () => {
      if (wasmReady.value) return;
      if (wasmInitPromiseRef.value) {
        await wasmInitPromiseRef.value;
        return;
      }

      isWasmLoading.value = true;
      wasmInitPromiseRef.value = (async () => {
        await prepareZXingModule({ fireImmediately: true });
        wasmReady.value = true;
      })();

      try {
        await wasmInitPromiseRef.value;
      } finally {
        isWasmLoading.value = false;
      }
    };

    const stopTracks = () => {
      if (streamRef.value) {
        streamRef.value.getTracks().forEach((t) => t.stop());
        streamRef.value = null;
      }
    };

    const cancelLoop = () => {
      if (rafRef.value !== null) {
        cancelAnimationFrame(rafRef.value);
        rafRef.value = null;
      }
    };

    const resetScannerState = async () => {
      errorMessage.value = '';
      scannedResult.value = '';
      detectedFormat.value = '';
      stableCandidate.value = '';
      stableCount.value = 0;
      isWasmLoading.value = false;
      decodingRef.value = false;
      cancelLoop();
      stopTracks();
      isScanning.value = false;
    };

    const finalizeDecoded = async (text: string, format: string) => {
      const trimmed = (text || '').trim();
      let outText = trimmed;
      let expirationDate: string | null = null;

      if (isCode128BarcodeFormat(format)) {
        const janFromCode128 = extractJanFromCode128(trimmed);
        if (janFromCode128) {
          outText = janFromCode128;
        }
        expirationDate = extractExpirationDateFromCode128(trimmed);
        console.log('[BarcodeScanner] Code128', {
          text: trimmed,
          format,
          resolvedForSearch: outText,
          expirationDate,
        });
      }

      const scanMeta: CodeScannedMeta = {
        rawText: trimmed,
        format,
        expirationDate,
      };
      scannedResult.value = outText;
      detectedFormat.value = format;
      emit('update:modelValue', outText);
      emit('code-scanned', outText, scanMeta);
      await stopScanning();
    };

    const decodeCurrentFrame = async () => {
      const video = videoRef.value;
      if (!video || !ctx || !isScanning.value) return;
      if (video.readyState < HTMLMediaElement.HAVE_ENOUGH_DATA) return;

      const width = video.videoWidth;
      const height = video.videoHeight;
      if (!width || !height) return;

      const cropHeight = Math.max(140, Math.round(height * 0.34));
      const cropTop = Math.round((height - cropHeight) / 2);

      canvas.width = width;
      canvas.height = cropHeight;
      ctx.drawImage(video, 0, cropTop, width, cropHeight, 0, 0, width, cropHeight);

      const imageData = ctx.getImageData(0, 0, width, cropHeight);
      const options: ReaderOptions = {
        formats: MODE_FORMATS[props.scanMode],
        tryHarder: true,
        maxNumberOfSymbols: 1,
      };

      const results = await readBarcodes(imageData, options);
      if (!results || results.length === 0) return;

      const decoded = (results[0].text || '').trim();
      const format = results[0].format || '';
      if (!decoded) return;

      if (decoded === stableCandidate.value) {
        stableCount.value += 1;
      } else {
        stableCandidate.value = decoded;
        stableCount.value = 1;
      }

      detectedFormat.value = format;

      if (stableCount.value >= REQUIRED_STABLE_COUNT) {
        await finalizeDecoded(decoded, format);
      }
    };

    const loopDecode = async () => {
      if (!isScanning.value) return;

      const now = Date.now();
      const intervalMs = props.scanMode === 'code128' ? 80 : 85;

      if (!decodingRef.value && now - lastDecodeAt.value >= intervalMs) {
        decodingRef.value = true;
        lastDecodeAt.value = now;
        try {
          await decodeCurrentFrame();
        } catch (err) {
          // Ignore decode errors per frame to keep scanner loop alive.
        } finally {
          decodingRef.value = false;
        }
      }

      if (isScanning.value) {
        rafRef.value = requestAnimationFrame(loopDecode);
      }
    };

    const startScanning = async () => {
      errorMessage.value = '';
      scannedResult.value = '';
      detectedFormat.value = '';
      stableCandidate.value = '';
      stableCount.value = 0;
      isScanning.value = true;

      await nextTick();

      try {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          throw new Error('お使いのブラウザはカメラアクセスをサポートしていません');
        }

        // iOS Safari: call getUserMedia as close to user gesture as possible.
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
          audio: false,
        });
        streamRef.value = stream;

        const video = videoRef.value;
        if (!video) {
          throw new Error('video要素が見つかりません');
        }
        video.srcObject = stream;
        await video.play();

        // Ensure wasm is ready before decode loop starts.
        await ensureWasmReady();

        rafRef.value = requestAnimationFrame(loopDecode);
      } catch (err: any) {
        await resetScannerState();
        errorMessage.value = err?.message || 'スキャンの開始に失敗しました。カメラの権限を確認してください';
      }
    };

    const stopScanning = async () => {
      cancelLoop();
      stopTracks();
      isScanning.value = false;
      emit('scan-stopped');
    };

    onBeforeUnmount(() => {
      stopScanning();
    });

    // Pre-warm wasm for faster first decode.
    ensureWasmReady().catch(() => {
      // If prewarm fails, startScanning will retry and surface error.
    });

    return {
      isScanning,
      scannedResult,
      detectedFormat,
      errorMessage,
      isWasmLoading,
      videoRef,
      startScanning,
      stopScanning,
      resetScannerState,
    };
  },
});
</script>

<style scoped>
.reader-container {
  position: relative;
  overflow: hidden;
  width: 280px;
  height: 160px;
  background-color: #f8f8f8;
}

.reader-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.scan-guide {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  z-index: 2;
}

.scan-window {
  position: relative;
  width: 240px;
  height: 120px;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.28);
}

.corner {
  position: absolute;
  width: 28px;
  height: 28px;
  border-color: #ffffff;
  border-style: solid;
  border-width: 0;
  filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.35));
}

.corner-tl {
  top: 0;
  left: 0;
  border-top-width: 6px;
  border-left-width: 6px;
}

.corner-tr {
  top: 0;
  right: 0;
  border-top-width: 6px;
  border-right-width: 6px;
}

.corner-bl {
  bottom: 0;
  left: 0;
  border-bottom-width: 6px;
  border-left-width: 6px;
}

.corner-br {
  right: 0;
  bottom: 0;
  border-right-width: 6px;
  border-bottom-width: 6px;
}

.scanner-overlay {
  position: absolute;
  inset: 0;
  background: rgba(17, 24, 39, 0.7);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #fff;
  gap: 10px;
  z-index: 3;
}

.overlay-text {
  font-size: 13px;
}

.spinner {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top-color: #ffffff;
  animation: spin 0.9s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.stop-button {
  padding: 8px 16px;
  border-radius: 4px;
  border: none;
  background-color: #f44336;
  color: white;
  cursor: pointer;
  font-size: 14px;
}

.result {
  padding: 8px;
  background-color: #f0f0f0;
  border-radius: 4px;
  font-size: 14px;
}

.error-message {
  padding: 8px;
  background-color: #ffebee;
  color: #d32f2f;
  border-radius: 4px;
  border-left: 4px solid #d32f2f;
  font-size: 14px;
}

@media (max-width: 640px) {
  .stop-button {
    padding: 6px 12px;
    font-size: 12px;
  }

  .result,
  .error-message {
    font-size: 12px;
  }
}
</style>